// apps/web/src/main.tsx
import React from 'react';
import ReactDOM from 'react-dom/client';
import { Provider } from 'react-redux';
import { store } from './store/index';
import { migrateFromLocalStorage } from './adapters';
import { validateCurriculum } from '@nisk/core/learning';
import { initializeEconomy, claimDailyLogin } from './store/economySlice';
import { initializeXp } from './store/xpSlice';
import { initializeBadges, evaluateBadgesThunk } from './store/badgesSlice';
import { startAutoSync } from './sync/webSync';
import App from './App/App';
import { registerAudioServiceWorker } from './lib/registerAudioSW';
import { installGlobalErrorHandlers } from './lib/globalErrorHandlers';
import './styles/App.scss';

// 0. Install window-level error/rejection handlers FIRST, before any boot step
//    can reject. They filter known browser-extension and Firebase-COOP console
//    noise and log everything else classified, so a real unhandled rejection
//    during startup is visible instead of silently swallowed.
installGlobalErrorHandlers();

(async () => {
	// 1. Migrate any legacy localStorage data into IndexedDB (idempotent)
	await migrateFromLocalStorage();

	// 2. Validate the curriculum on dev boot (warnings only, won't block)
	const v = validateCurriculum();
	if (!v.ok) console.error('Curriculum errors:', v.errors);
	if (v.warnings.length) console.warn('Curriculum warnings:', v.warnings);

	// 3. Hydrate XP/level from storage (forward-only; carries existing XP)
	store.dispatch(initializeXp());

	// 4. Boot the gem economy from local storage, then claim daily login.
	//    Daily login can advance the streak — which is exactly the kind of
	//    ambient state that must be captured in the badge baseline below, so
	//    it has to run BEFORE the retroactive sweep.
	await store.dispatch(initializeEconomy());
	await store.dispatch(claimDailyLogin());

	// 5. Hydrate earned badges, then run a SILENT retroactive sweep so users
	//    who already completed lessons / passed quizzes / mastered modules /
	//    hit streak or level milestones are credited on boot. The sweep never
	//    toasts (retroactive: true) — history is recognized quietly.
	//
	//    This sweep is AWAITED on purpose. Its fulfillment records the badge
	//    "boot baseline" (every id provable from durable history right now).
	//    Live evaluations suppress toasts for anything in that baseline, so
	//    ambient-state badges (FI number, streak, level) can never be
	//    celebrated by an unrelated live event after a boot race. Awaiting it
	//    before render guarantees the baseline is complete before the user can
	//    trigger any live reward event.
	store.dispatch(initializeBadges());
	await store.dispatch(evaluateBadgesThunk({ retroactive: true }));

	// 6. Start the sync engine's auto-triggers (initial catch-up once auth
	//    resolves + push on regaining connectivity). Idempotent; subscribes to
	//    auth state, so it's safe to call before the user has signed in.
	startAutoSync();

	// 7. Render the app
	ReactDOM.createRoot(document.getElementById('root') as HTMLElement).render(
		<React.StrictMode>
			<Provider store={store}>
				<App />
			</Provider>
		</React.StrictMode>,
	);

	// 8. Register the audio service worker (prod only). It intercepts lesson
	//    audio MP3s cache-first so downloaded — or simply previously-streamed —
	//    lessons play offline with no extra work. Best-effort; never blocks.
	registerAudioServiceWorker();
})();
